import { Component, Inject, type OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  BdcContact,
  bdcContactJobTitles,
  BdcContactPhone,
  BdcContactProfileType,
  BdcContentV2Service,
  bdcLanguages,
  BdcPhoneTechType,
  bdcPhoneTechTypeOptions,
} from '@tokeet-frontend/channel-content'
import { SaveForm, User, userFullname, ActionFailed } from '@tokeet-frontend/tv3-platform'
import { startWith, finalize } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { Toaster } from '@tokeet-frontend/tv3-platform'

@Component({
  selector: 'app-bdc-create-property-contact-dialog',
  templateUrl: './bdc-create-property-contact-dialog.component.html',
  styleUrls: ['./bdc-create-property-contact-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class BdcCreatePropertyContactDialogComponent implements OnInit {
  userCtrl = new FormControl()

  addressForm = this.fb.group({
    address_line: ['', [Validators.required]],
    city_name: ['', [Validators.required]],
    country_code: ['', [Validators.required]],
    postal_code: [''],
    language_code: [''],
  })

  personForm = this.fb.group({
    name: ['', [Validators.required]],
    job_title: ['Unknown', [Validators.required]],
    gender: [],
    language_code: ['', [Validators.required]],
  })

  form = this.fb.group({
    contact_profiles: [null, [Validators.required]],
    phones: this.fb.array([]),
    email: ['', [Validators.required, Validators.email]],
    contact_person: this.personForm,
    address: this.addressForm,
  })
  jobs = bdcContactJobTitles
  languages = bdcLanguages

  get phonesArray() {
    return this.form.get('phones') as FormArray
  }

  contactProfileTypes: BdcContactProfileType[] = []
  phoneTechTypes = bdcPhoneTechTypeOptions

  isLoading = false
  constructor(
    private dialogRef: MatDialogRef<BdcCreatePropertyContactDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private store: Store<any>,
    private contentService: BdcContentV2Service,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      contact?: BdcContact
      propertyId: number
      otherContacts: BdcContact[]
      isGeneralMissing: boolean
      contactIndex?: number
    }
  ) {
    if (!this.data.isGeneralMissing) {
      this.contactProfileTypes = Object.values(BdcContactProfileType).filter(
        (type) => type !== BdcContactProfileType.GENERAL
      )
    } else {
      this.contactProfileTypes = Object.values(BdcContactProfileType)
    }
  }

  close() {
    this.dialogRef.close()
  }

  ngOnInit(): void {
    if (this.data?.contact) {
      this.form.patchValue({
        ...this.data.contact,
        contact_profiles: this.data.contact.contact_profiles.map((t) => t.type),
      })
      this.data.contact.phones?.forEach((phone) => {
        this.phonesArray.push(this.createPhoneForm(phone))
      })
    } else {
      this.phonesArray.push(this.createPhoneForm())
    }
  }

  createPhoneForm(item?: BdcContactPhone) {
    const form = this.fb.group({
      phone_number: [item?.phone_number, [Validators.required]],
      phone_tech_type: [item?.phone_tech_type, [Validators.required]],
      extension: [item?.extension],
    })
    form
      .get('phone_tech_type')
      .valueChanges.pipe(startWith(item?.phone_tech_type))
      .subscribe((value) => {
        if (value === BdcPhoneTechType.VOICE) {
          form.get('extension').setValidators([Validators.required])
          form.get('extension').enable()
        } else {
          form.get('extension').clearValidators()
          form.get('extension').disable()
          form.get('extension').reset()
        }
      })
    return form
  }

  onAddPhone() {
    this.phonesArray.push(this.createPhoneForm())
  }

  onRemovePhone(index: number) {
    this.phonesArray.removeAt(index)
  }

  onUserSelect([user]: User[]) {
    this.form.patchValue({
      contact_person: {
        name: userFullname(user),
        language_code: user.attributes?.language,
        gender: user.attributes?.gender,
      },
      email: user.primaryEmail,
      address: {
        address_line: user.address?.address,
        city_name: user.address?.city,
        country_code: user.address?.countryCode,
        postal_code: user.address?.postalCode,
        language_code: user.attributes?.language,
      },
    })
    if (user.phone) {
      this.phonesArray.clear()
      this.phonesArray.push(
        this.createPhoneForm({
          phone_number: String(user.phone).replace(/\s+/g, ''),
          phone_tech_type: BdcPhoneTechType.MOBILE,
        })
      )
    }
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { contact_profiles, contact_person, address, ...payload } = this.form.getRawValue()
    const contact = {
      ...payload,
      contact_person,
      address: {
        ...address,
        language_code: contact_person.language_code,
      },
      contact_profiles: contact_profiles.map((t) => ({ type: t })),
    }
    const contacts = [...this.data.otherContacts]
    if (this.data.contactIndex > -1) {
      contacts.splice(this.data.contactIndex, 0, contact)
    } else {
      contacts.push(contact)
    }
    if (
      this.data.isGeneralMissing &&
      !contacts.find((c) => c.contact_profiles.find((p) => p.type === BdcContactProfileType.GENERAL))
    ) {
      this.toaster.error($localize`General Contact is required before adding other contacts.`)
      return
    }

    this.isLoading = true
    this.contentService
      .updatePropertyContacts(this.data.propertyId, { contacts })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          if (this.data.contact) {
            this.toaster.success($localize`Contact updated successfully`)
          } else {
            this.toaster.success($localize`Contact added successfully`)
          }
          this.dialogRef.close(data.contacts)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
